/**
 * Doctor Store - Healthcare Provider Management
 *
 * Manages doctor/provider data for the current clinic context.
 * Provides hooks for accessing and updating doctor information.
 */

import { useShallow } from "zustand/react/shallow";
import type { AsyncStoreActions, BaseStore } from "../../core";
import { createAsyncActions, createStore, withLogging } from "../../core";

// Doctor interface (matching the actual API response)
export interface Doctor {
  id?: string;
  fullName?: string;
  phoneNumber?: string;
  emailAddress?: string;
  mcrNo?: string;
}

// Doctor store state interface
interface DoctorStoreState extends BaseStore, AsyncStoreActions {
  // Data
  currentDoctor: Doctor | null;

  // Actions
  setCurrentDoctor: (doctor: Doctor | null) => void;
  updateCurrentDoctor: (updates: Partial<Doctor>) => void;
  reset: () => void;
}

// Initial state
const initialState = {
  currentDoctor: null,
};

// Create the doctor store
export const useDoctorStore = createStore<DoctorStoreState>(
  withLogging("DoctorStore", (set, _get) => ({
    ...initialState,
    ...createAsyncActions<DoctorStoreState>(),
    _initialized: false,
    _lastUpdated: Date.now(),

    // Basic actions
    setCurrentDoctor: (doctor: Doctor | null) =>
      set((state) => {
        state.currentDoctor = doctor;
        state._lastUpdated = Date.now();
      }),

    updateCurrentDoctor: (updates: Partial<Doctor>) =>
      set((state) => {
        if (state.currentDoctor) {
          Object.assign(state.currentDoctor, updates);
          state._lastUpdated = Date.now();
        }
      }),

    reset: () =>
      set((state) => {
        state.currentDoctor = null;
        state.error = null;
        state._lastUpdated = Date.now();
      }),
  })),
  {
    name: "doctor-store",
    // Not persisted (ECHO-2318): the current doctor carries staff PII (phone, email, MCR no.).
    // The consultation page repopulates it from the current-staff API on every mount.
    persist: false,
  },
);

// ===== SIMPLIFIED DOMAIN HOOK =====
/**
 * Get doctor store state (simplified access)
 * @returns Complete doctor state
 */
export const useDoctor = () => useDoctorStore((state) => state);

// ===== DATA SELECTORS =====
/**
 * Get current doctor (from useGetDoctorsByCurrent API)
 * @returns Current doctor or null
 */
export const useCurrentDoctor = () => useDoctorStore((state) => state.currentDoctor);

// ===== STATE SELECTORS =====
/**
 * Get doctor loading state
 * @returns True if doctor operations are loading
 */
export const useDoctorLoading = () => useDoctorStore((state) => state.loading);

/**
 * Get doctor error state
 * @returns Error message or null
 */
export const useDoctorError = () => useDoctorStore((state) => state.error);

// ===== ACTION HOOKS =====
/**
 * Get doctor action functions
 * @returns Object containing all doctor actions
 */
export const useDoctorActions = () =>
  useDoctorStore(
    useShallow((state) => ({
      setCurrentDoctor: state.setCurrentDoctor,
      updateCurrentDoctor: state.updateCurrentDoctor,
      reset: state.reset,
      setLoading: state.setLoading,
      setError: state.setError,
      clearError: state.clearError,
    })),
  );

/**
 * Individual action selectors for stable references
 */
export const useSetCurrentDoctor = () => useDoctorStore((state) => state.setCurrentDoctor);
export const useUpdateCurrentDoctor = () => useDoctorStore((state) => state.updateCurrentDoctor);
export const useSetDoctorLoading = () => useDoctorStore((state) => state.setLoading);
export const useSetDoctorError = () => useDoctorStore((state) => state.setError);

/**
 * Get current doctor state (non-reactive)
 * @returns Current doctor or null
 */
export const getCurrentDoctor = () => useDoctorStore.getState().currentDoctor;
